/**
 * PlayerPageMobileFabContainer — logic for the mobile player FAB (Notion 8.6,
 * SE-5083). Mobile sibling of the desktop PlayerHeroCtaCluster (SE-5022).
 *
 * Owns the expand/collapse state and the entrance animation: a single driver
 * `Animated.Value` (0 collapsed → 1 expanded) feeds the `+`→`✕` rotation and,
 * via per-index input ranges, the staggered translateY/opacity of the three
 * action pills (~200ms total). Each action re-uses an existing flow:
 *   1. Update list / Add to watchlist → onAddToList (Epic 4 / SRD-6 sheet).
 *      Label is conditional on watchlist membership (Notion 8.6).
 *   2. Write a note → flips the visible tab to Reports & Notes and signals the
 *      auto-create reactive variable (same path as desktop handleSelectNote).
 *   3. Create a report → onAddReport (matches selection).
 *
 * Out of scope (per ticket): manual-player Edit/Delete/Transfer admin actions —
 * those stay on MobileActionBar; this FAB is the consumer-player surface only.
 * @remarks Container component — owns animation refs, state, and handlers.
 */

import { useCallback, useMemo, useState } from "react";
// design-system: rn-primitive waiver — Animated driver for FAB transitions
import { Animated } from "react-native";

import AddToPipelineDialog from "@/features/player-kanban/components/organisms/AddToPipelineDialog";

import { playerAutoCreateNoteVar, selectPlayerTab } from "@/stores/player";

import { TEST_ID_PLAYER_DETAIL } from "e2e/testId";

import PlayerPageMobileFabView, {
  type FabAction,
} from "./PlayerPageMobileFabView";

/** Total expand/collapse duration (~200ms staggered) — Notion 8.6 feel */
const ANIMATION_DURATION_MS = 200;

/** Per-pill stagger window as a fraction of the 0→1 driver */
const STAGGER_STEP = 0.2;

/** Pixels each pill travels up on entrance */
const TRAVEL_DISTANCE = 12;

/**
 * Props for PlayerPageMobileFabContainer
 */
export interface PlayerPageMobileFabContainerProps {
  /** Number of watchlists the player belongs to — drives the conditional first label */
  readonly watchlistCount: number;
  /** Opens the list-membership flow (Epic 4 / SRD-6 mobile sheet) */
  readonly onAddToList: () => void;
  /** Opens the report creation flow (matches selection) */
  readonly onAddReport: () => void;
  /**
   * Player id — SE-5499. Enables the "Add to Pipeline" FAB action, which opens
   * the reusable pick-pipeline-and-stage dialog. The FAB only renders for a
   * loaded player, so this is always present at the call site.
   */
  readonly playerId: string;
  /** Optional player display name shown in the pipeline dialog subtitle. */
  readonly playerName?: string;
}

/**
 * Build the per-pill animated entrance style for a given stack index.
 * Index 0 is the bottom-most pill (closest to the FAB) and animates first.
 * @param driver - The shared 0→1 expansion driver
 * @param index - Stack index from the bottom (0-based)
 */
const buildActionStyle = (driver: Animated.Value, index: number) => {
  const start = index * STAGGER_STEP;
  const end = Math.min(start + STAGGER_STEP, 1);
  return {
    opacity: driver.interpolate({
      inputRange: [start, end],
      outputRange: [0, 1],
      extrapolate: "clamp" as const,
    }),
    transform: [
      {
        translateY: driver.interpolate({
          inputRange: [start, end],
          outputRange: [TRAVEL_DISTANCE, 0],
          extrapolate: "clamp" as const,
        }),
      },
    ],
  };
};

/**
 * PlayerPageMobileFabContainer renders the floating mobile player FAB
 * @param props - Container props
 * @param props.watchlistCount - Number of watchlists the player belongs to
 * @param props.onAddToList - Opens the list-membership flow
 * @param props.onAddReport - Opens the report creation flow
 * @param props.playerId - Player id for the "Add to Pipeline" action (SE-5499)
 * @param props.playerName - Optional player name shown in the pipeline dialog
 * @returns PlayerPageMobileFabView wired with animated state and handlers
 */
const PlayerPageMobileFabContainer = ({
  watchlistCount,
  onAddToList,
  onAddReport,
  playerId,
  playerName,
}: PlayerPageMobileFabContainerProps) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isPipelineDialogOpen, setIsPipelineDialogOpen] = useState(false);
  // Lazily-initialised once and never replaced. Held in state (not a ref) so
  // the interpolate() reads below are legitimate render-time reads — the
  // react-hooks/refs rule forbids reading a ref's value during render.
  const [driver] = useState(() => new Animated.Value(0));

  /** Drive the shared 0↔1 value, then commit the mounted/unmounted state */
  const animateTo = useCallback(
    (toValue: number, onDone?: () => void) => {
      Animated.timing(driver, {
        toValue,
        duration: ANIMATION_DURATION_MS,
        useNativeDriver: true,
      }).start(() => onDone?.());
    },
    [driver]
  );

  const handleExpand = useCallback(() => {
    setIsExpanded(true);
    animateTo(1);
  }, [animateTo]);

  const handleCollapse = useCallback(() => {
    // Flip the mounted state synchronously so collapse is deterministic even
    // when the animation's completion callback is delayed or never fires (e.g.
    // a dropped frame, or the native driver in a test env). The reverse
    // animation still plays for the FAB icon's ✕→+ rotation.
    setIsExpanded(false);
    animateTo(0);
  }, [animateTo]);

  const handleToggle = useCallback(() => {
    if (isExpanded) {
      handleCollapse();
    } else {
      handleExpand();
    }
  }, [isExpanded, handleExpand, handleCollapse]);

  /** Collapse, then invoke the supplied flow opener */
  const runAction = useCallback(
    (open: () => void) => {
      handleCollapse();
      open();
    },
    [handleCollapse]
  );

  const handleListAction = useCallback(
    () => runAction(onAddToList),
    [runAction, onAddToList]
  );

  const handleReportAction = useCallback(
    () => runAction(onAddReport),
    [runAction, onAddReport]
  );

  const handleNoteAction = useCallback(
    () =>
      runAction(() => {
        // SE-8285: the FAB acts on the player whose page it is rendered on.
        selectPlayerTab(playerId, "reportsNotes");
        playerAutoCreateNoteVar(true);
      }),
    [runAction, playerId]
  );

  /** Open the reusable pick-pipeline-and-stage dialog (SE-5499). */
  const handlePipelineAction = useCallback(
    () => runAction(() => setIsPipelineDialogOpen(true)),
    [runAction]
  );

  const handleClosePipelineDialog = useCallback(
    () => setIsPipelineDialogOpen(false),
    []
  );

  /** First label is conditional on watchlist membership (Notion 8.6) */
  const listLabel = watchlistCount === 0 ? "Add to watchlist" : "Update list";

  /**
   * Actions render top-to-bottom in the View, so the top pill is the last to
   * animate and the list pill (bottom, nearest the FAB) animates first. Stagger
   * index is therefore assigned bottom-up. SE-5499 adds "Add to Pipeline" as the
   * new top-most pill (index 3) — the per-index interpolation windows extend to
   * cover it.
   */
  const actions: readonly FabAction[] = useMemo(
    () => [
      {
        testID: TEST_ID_PLAYER_DETAIL.MOBILE_FAB_ACTION_PIPELINE,
        label: "Add to Pipeline",
        onPress: handlePipelineAction,
        animatedStyle: buildActionStyle(driver, 3),
      },
      {
        testID: TEST_ID_PLAYER_DETAIL.MOBILE_FAB_ACTION_REPORT,
        label: "Create a report",
        onPress: handleReportAction,
        animatedStyle: buildActionStyle(driver, 2),
      },
      {
        testID: TEST_ID_PLAYER_DETAIL.MOBILE_FAB_ACTION_NOTE,
        label: "Write a note",
        onPress: handleNoteAction,
        animatedStyle: buildActionStyle(driver, 1),
      },
      {
        testID: TEST_ID_PLAYER_DETAIL.MOBILE_FAB_ACTION_LIST,
        label: listLabel,
        onPress: handleListAction,
        animatedStyle: buildActionStyle(driver, 0),
      },
    ],
    [
      driver,
      listLabel,
      handlePipelineAction,
      handleReportAction,
      handleNoteAction,
      handleListAction,
    ]
  );

  const iconStyle = useMemo(
    () => ({
      transform: [
        {
          rotate: driver.interpolate({
            inputRange: [0, 1],
            outputRange: ["0deg", "45deg"],
          }),
        },
      ],
    }),
    [driver]
  );

  const fabAccessibilityState = useMemo(
    () => ({ expanded: isExpanded }),
    [isExpanded]
  );

  return (
    <>
      <PlayerPageMobileFabView
        isExpanded={isExpanded}
        onToggle={handleToggle}
        onBackdropPress={handleCollapse}
        actions={actions}
        iconStyle={iconStyle}
        fabAccessibilityState={fabAccessibilityState}
      />
      {/* key flips on open so the dialog remounts with a fresh board/stage
          selection each time (key-reset — see PlayerHeroCtaCluster). */}
      <AddToPipelineDialog
        key={isPipelineDialogOpen ? "atp-open" : "atp-closed"}
        isOpen={isPipelineDialogOpen}
        onClose={handleClosePipelineDialog}
        playerId={playerId}
        playerName={playerName}
      />
    </>
  );
};

PlayerPageMobileFabContainer.displayName = "PlayerPageMobileFabContainer";

export default PlayerPageMobileFabContainer;
